import { CSSProperties, memo } from 'react';

import bytes from 'bytes';
import { injectIntl, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import InlineMessage from '@atlaskit/inline-message';

import { getSizeLimits } from 'src/selectors/repository-selectors';

import { RepositoryPrivilege } from '../types';
import urls from '../urls';

import messages from './large-repository-warning.i18n';

type LargeRepositoryWarningProps = {
  isReadOnly?: boolean;
  size?: number;
  accessLevel?: RepositoryPrivilege;
};

const LargeRepositoryWarning = memo(
  ({
    size = 0,
    isReadOnly = false,
    accessLevel,
  }: LargeRepositoryWarningProps) => {
    const sizeLimits = useSelector(getSizeLimits);

    if (size < sizeLimits.soft) {
      return null;
    }

    const softLimit = bytes(sizeLimits.soft, { unitSeparator: ' ' });
    const hardLimit = bytes(sizeLimits.hard, { unitSeparator: ' ' });

    const panelBodyWrapperStyles = {
      // Panel width is based on sidebar width. Increase width for readability.
      minWidth: '272px',
      // Override `white-space: nowrap` rule in DetailsCard <Value /> component.
      whiteSpace: 'normal',
    } as CSSProperties;

    const PanelBody = () => {
      if (size >= sizeLimits.hard && isReadOnly) {
        return (
          <>
            <FormattedMessage
              tagName="h4"
              {...messages.readOnlyHeading}
              values={{ hardLimit }}
            />
            <FormattedMessage
              tagName="p"
              {...messages.readOnlyDescriptionLine1}
              values={{ hardLimit }}
            />
            <FormattedMessage
              tagName="p"
              {...messages.readOnlyDescriptionLine2}
              values={{
                hardLimit,
                linkText: (
                  <a href={urls.external.reduceRepositorySize} target="_blank">
                    <FormattedMessage
                      {...messages.readOnlyDescriptionLine2LinkText}
                    />
                  </a>
                ),
              }}
            />
          </>
        );
      }

      if (size >= sizeLimits.hard) {
        return (
          <>
            <FormattedMessage
              tagName="h4"
              {...messages.hardLimitHeading}
              values={{ hardLimit }}
            />
            {accessLevel === 'admin' ? (
              <FormattedMessage
                tagName="p"
                {...messages.hardLimitDescriptionLine1Admin}
                values={{ hardLimit }}
              />
            ) : (
              <FormattedMessage
                tagName="p"
                {...messages.hardLimitDescriptionLine1}
                values={{ hardLimit }}
              />
            )}
            <FormattedMessage
              tagName="p"
              {...messages.hardLimitDescriptionLine2}
              values={{
                hardLimit,
                linkText: (
                  <a href={urls.external.reduceRepositorySize} target="_blank">
                    <FormattedMessage
                      {...messages.hardLimitDescriptionLine2LinkText}
                    />
                  </a>
                ),
              }}
            />
          </>
        );
      }

      return (
        <>
          <FormattedMessage tagName="h4" {...messages.softLimitHeading} />
          {accessLevel === 'admin' ? (
            <FormattedMessage
              tagName="p"
              {...messages.softLimitDescriptionAdmin}
              values={{ softLimit, hardLimit }}
            />
          ) : (
            <FormattedMessage
              tagName="p"
              {...messages.softLimitDescription}
              values={{ softLimit, hardLimit }}
            />
          )}
          <br />
          <CustomThemeButton
            appearance="link"
            href={urls.external.reduceRepositorySize}
            spacing="none"
            target="_blank"
          >
            <FormattedMessage {...messages.softLimitLink} />
          </CustomThemeButton>
        </>
      );
    };

    return (
      <InlineMessage
        placement="left-start"
        // @ts-ignore InlineMessage should accept Element as title
        title={
          <FormattedMessage
            {...messages.separator}
            values={{
              currentSize: bytes(size, { unitSeparator: ' ' }),
              maxSize: hardLimit,
            }}
          />
        }
        appearance="warning"
      >
        <div style={{ ...panelBodyWrapperStyles }}>
          <PanelBody />
        </div>
      </InlineMessage>
    );
  }
);

export default injectIntl(LargeRepositoryWarning);
